import { useEffect, useMemo, useState } from "react";
import { TopBar } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Globe, Loader2, Download, Save, Plus, X, ArrowRight, AlertTriangle, ChevronsUpDown, Check, PencilLine } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { nsGet, nsSet, normalizeDomain, websiteDomainKey, type AmWorkspace } from "@/services/aftermarketService";
import { useCanAccessSideprojects } from "@/hooks/useCanAccessSideprojects";
import { useWebsites } from "@/hooks/useWebsites";
import { logPublic } from "@/services/contentEngine/contentLogger";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BulkNsChange } from "@/components/dns/BulkNsChange";

const DnsChangePage = () => {
  const { toast } = useToast();
  const [workspace, setWorkspace] = useState<AmWorkspace>("firma");
  // Tryb: jedna domena (jak dotad) albo bulk (wiele domen, wspolne/osobne NS)
  const [mode, setMode] = useState<"single" | "bulk">("single");
  const [domain, setDomain] = useState("");
  const [currentNs, setCurrentNs] = useState<string[] | null>(null);
  const [editNs, setEditNs] = useState<string[]>(["", ""]);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Sponsoraki = projekt wlasciciela i szefa. Konto AfterMarket Sponsorakow
  // dostepne TYLKO z flaga allowed_users.can_access_sideprojects (ta sama,
  // ktora chowa ich domeny i mapy). Pracownicy widza wylacznie konto firmowe.
  const { canAccess: canSideprojects } = useCanAccessSideprojects();
  useEffect(() => {
    if (canSideprojects === false && workspace === "sideprojects") setWorkspace("firma");
  }, [canSideprojects, workspace]);
  // Twardy guard poza UI — nawet gdyby stan workspace zostal podmieniony.
  const effectiveWorkspace: AmWorkspace = canSideprojects === true ? workspace : "firma";

  // Lista domen z panelu /domeny dla WYBRANEGO konta. RLS na `websites`
  // (can_see_workspace) i tak nie zwroci Sponsorakow bez flagi — lista jest
  // wiec bezpieczna z definicji, nie tylko przez filtr w UI.
  const { websites, loading: websitesLoading } = useWebsites();
  const accountDomains = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{ key: string; name: string; websiteId: string; expiry: string | null; source: string | null }> = [];
    for (const w of websites) {
      if ((w.workspace || "firma") !== effectiveWorkspace) continue;
      const key = websiteDomainKey(w);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      const exp = (w.expiry_date || "").trim();
      out.push({ key, name: w.name, websiteId: w.id, expiry: exp && exp.toUpperCase() !== "EMPTY" ? exp : null, source: w.expiry_source ?? null });
    }
    return out.sort((a, b) => a.key.localeCompare(b.key));
  }, [websites, effectiveWorkspace]);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const selectedKey = normalizeDomain(domain);
  const selectedFromList = accountDomains.find((d) => d.key === selectedKey) ?? null;

  const pickDomain = (key: string) => {
    setDomain(key);
    setCurrentNs(null);
    setPickerOpen(false);
  };

  // Zmiana konta = inna lista, wiec wybrana domena i pobrane NS przestaja byc aktualne.
  const changeWorkspace = (ws: AmWorkspace) => {
    setWorkspace(ws);
    setDomain("");
    setCurrentNs(null);
  };

  const handleFetch = async () => {
    const d = normalizeDomain(domain);
    if (!d) {
      toast({ title: "Podaj poprawną domenę", variant: "destructive" });
      return;
    }
    setFetching(true);
    setCurrentNs(null);
    try {
      const ns = await nsGet(d, effectiveWorkspace);
      setCurrentNs(ns);
      setEditNs(ns.length >= 2 ? [...ns] : [...ns, ...Array(2 - ns.length).fill("")]);
      toast({ title: "Pobrano nameservery", description: `${d}: ${ns.length ? ns.join(", ") : "brak ustawionych"}` });
    } catch (err) {
      toast({ title: "Nie udało się pobrać NS", description: err instanceof Error ? err.message : String(err), variant: "destructive" });
    } finally {
      setFetching(false);
    }
  };

  const cleanEdit = editNs.map((n) => n.trim()).filter(Boolean);

  const handleSet = async () => {
    setConfirmOpen(false);
    setSaving(true);
    try {
      const ok = await nsSet(domain, cleanEdit, effectiveWorkspace);
      if (ok) {
        logPublic("AfterMarket", `[${effectiveWorkspace === "sideprojects" ? "Sponsoraki" : "Firma"}] NS zmienione: ${normalizeDomain(domain)} → ${cleanEdit.join(", ")} (było: ${currentNs?.length ? currentNs.join(", ") : "brak"})`);
        toast({ title: "Nameservery zmienione ✓", description: "AfterMarket przyjął zmianę. Propagacja DNS może potrwać do 24h." });
        setCurrentNs([...cleanEdit]);
      } else {
        toast({ title: "AfterMarket nie potwierdził zmiany", variant: "destructive" });
      }
    } catch (err) {
      toast({ title: "Zmiana NS nie powiodła się", description: err instanceof Error ? err.message : String(err), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const setNsAt = (i: number, val: string) => setEditNs((prev) => prev.map((n, idx) => (idx === i ? val : n)));

  return (
    <>
      <TopBar title="Zmiana DNS" />
      <div className="flex-1 overflow-auto p-6">
        <Card className={mode === "bulk" ? "max-w-6xl" : "max-w-2xl"}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Globe className="h-4 w-4" />Zmiana nameserverów przez AfterMarket
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Pobierz obecne NS domeny i ustaw nowe (np. te z Cloudflare). <strong>Zły NS = domena offline</strong> —
              zmiana wymaga potwierdzenia.
            </p>
            <Tabs value={mode} onValueChange={(v) => setMode(v as "single" | "bulk")} className="pt-1">
              <TabsList className="h-8">
                <TabsTrigger value="single" className="text-xs">Pojedyncza domena</TabsTrigger>
                <TabsTrigger value="bulk" className="text-xs">Bulk — wiele domen</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          {mode === "bulk" ? (
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label>Konto</Label>
              <Select value={effectiveWorkspace} onValueChange={(v) => changeWorkspace(v as AmWorkspace)}>
                <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="firma">🏢 Firma</SelectItem>
                  {/* Tylko dla uprawnionych do Sponsorakow — pracownicy nie widza tej opcji */}
                  {canSideprojects && <SelectItem value="sideprojects">🎨 Sponsoraki</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            {/* key = konto: zmiana konta czysci zaznaczenie, pobrane NS i plan (inne konto AfterMarket) */}
            <BulkNsChange key={effectiveWorkspace} workspace={effectiveWorkspace} domains={accountDomains} loading={websitesLoading} />
          </CardContent>
          ) : (
          <CardContent className="space-y-5">
            <div className="grid grid-cols-[auto_1fr] gap-3 items-end">
              <div className="space-y-2">
                <Label>Konto</Label>
                <Select value={effectiveWorkspace} onValueChange={(v) => changeWorkspace(v as AmWorkspace)}>
                  <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="firma">🏢 Firma</SelectItem>
                    {/* Tylko dla uprawnionych do Sponsorakow — pracownicy nie widza tej opcji */}
                    {canSideprojects && <SelectItem value="sideprojects">🎨 Sponsoraki</SelectItem>}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="dns-domain">
                    Domena
                    {!websitesLoading && (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {accountDomains.length} na koncie {effectiveWorkspace === "sideprojects" ? "Sponsoraki" : "Firma"}
                      </span>
                    )}
                  </Label>
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                    onClick={() => { setManualMode((m) => !m); setCurrentNs(null); }}
                  >
                    <PencilLine className="h-3 w-3" />
                    {manualMode ? "Wybierz z listy" : "Wpisz ręcznie"}
                  </button>
                </div>

                {manualMode ? (
                  <Input
                    id="dns-domain"
                    value={domain}
                    onChange={(e) => { setDomain(e.target.value); setCurrentNs(null); }}
                    placeholder="przyklad.pl (domena spoza panelu)"
                  />
                ) : (
                  <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="dns-domain"
                        variant="outline"
                        role="combobox"
                        aria-expanded={pickerOpen}
                        className="w-full justify-between font-normal"
                        disabled={websitesLoading}
                      >
                        {websitesLoading ? (
                          <span className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Ładuję domeny…</span>
                        ) : selectedFromList ? (
                          <span className="flex items-center gap-2 min-w-0">
                            <span className="font-mono truncate">{selectedFromList.key}</span>
                            {selectedFromList.expiry && (
                              <span className="text-xs text-muted-foreground shrink-0">wygasa {selectedFromList.expiry}</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">
                            {accountDomains.length ? "Wybierz domenę z listy…" : "Brak domen na tym koncie w panelu — wpisz ręcznie"}
                          </span>
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[320px]" align="start">
                      <Command>
                        <CommandInput placeholder="Szukaj domeny…" />
                        <CommandList className="max-h-72">
                          <CommandEmpty>Nie ma takiej domeny na tym koncie.</CommandEmpty>
                          <CommandGroup>
                            {accountDomains.map((d) => (
                              <CommandItem key={d.key} value={d.key} onSelect={() => pickDomain(d.key)} className="flex items-center gap-2">
                                <Check className={"h-4 w-4 shrink-0 " + (d.key === selectedKey ? "opacity-100" : "opacity-0")} />
                                <span className="font-mono text-sm truncate flex-1">{d.key}</span>
                                {d.expiry ? (
                                  <span className="text-[11px] text-muted-foreground shrink-0">{d.expiry}</span>
                                ) : (
                                  <span className="text-[11px] text-muted-foreground/60 shrink-0">brak daty</span>
                                )}
                                {d.source === "not_on_account" && (
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-500/40 text-amber-400 shrink-0">
                                    brak na AfterMarket
                                  </Badge>
                                )}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>

            <Button onClick={handleFetch} disabled={fetching || !domain.trim()} variant="outline">
              {fetching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Pobierz obecne NS
            </Button>

            {currentNs !== null && (
              <>
                <div className="rounded-lg border p-3 bg-muted/20">
                  <Label className="text-xs text-muted-foreground">Obecne nameservery</Label>
                  {currentNs.length ? (
                    <div className="mt-1 space-y-1">
                      {currentNs.map((ns) => <div key={ns} className="font-mono text-sm">{ns}</div>)}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground mt-1">Brak ustawionych NS.</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">Nowe nameservery (min. 2)</Label>
                  {editNs.map((ns, i) => (
                    <div key={i} className="flex gap-2">
                      <Input value={ns} onChange={(e) => setNsAt(i, e.target.value)} placeholder={`ns${i + 1}.cloudflare.com`} className="font-mono text-sm" />
                      {editNs.length > 2 && (
                        <Button variant="ghost" size="sm" onClick={() => setEditNs((prev) => prev.filter((_, idx) => idx !== i))}><X className="h-4 w-4" /></Button>
                      )}
                    </div>
                  ))}
                  <Button variant="ghost" size="sm" onClick={() => setEditNs((prev) => [...prev, ""])}><Plus className="h-4 w-4 mr-1" />Dodaj NS</Button>
                </div>

                <Button onClick={() => setConfirmOpen(true)} disabled={saving || cleanEdit.length < 2}>
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Zmień nameservery
                </Button>
              </>
            )}
          </CardContent>
          )}
        </Card>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" />Potwierdź zmianę NS dla {normalizeDomain(domain)}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-3 text-xs">
                  <div className="flex-1">
                    <div className="text-muted-foreground mb-1">Obecne</div>
                    {(currentNs?.length ? currentNs : ["—"]).map((n) => <div key={n} className="font-mono">{n}</div>)}
                  </div>
                  <ArrowRight className="h-4 w-4 mt-4 text-muted-foreground shrink-0" />
                  <div className="flex-1">
                    <div className="text-muted-foreground mb-1">Nowe</div>
                    {cleanEdit.map((n) => <div key={n} className="font-mono text-emerald-400">{n}</div>)}
                  </div>
                </div>
                <p className="text-amber-400">Błędne NS mogą wyłączyć domenę do czasu poprawy. Kontynuować?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={handleSet}>Tak, zmień NS</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DnsChangePage;
